import { apiURL, companyId, locationId, murHost, cancelUrl, overrides } from "@/setsterConfig";
import { uuidv4, validateGUID } from "@/utilities";
import moment from "moment";

const SERVICES_CACHE_TTL = 5 * 60 * 1000;
let servicesCache = null;
let servicesCacheTime = 0;
let servicesRequest = null;

export default {
  /**
   * Retrieve a list of available services
   */
  listServices() {
    const now = Date.now();

    if (servicesCache && now - servicesCacheTime < SERVICES_CACHE_TTL) {
      return Promise.resolve(servicesCache);
    }

    // Share a request that is already in progress.
    if (servicesRequest) return servicesRequest;

    let url = apiURL + "service";
    url += "?company_id=" + companyId;
    url += "&location_id=" + locationId;

    servicesRequest = new Promise((resolve) => {
      let xhr = new XMLHttpRequest();

      xhr.open("GET", url, true);
      xhr.onload = function () {
        try {
          let data = JSON.parse(xhr.responseText).data;

          data = data.map((obj) => {
            if (overrides.duration[obj.id]) {
              obj.duration = overrides.duration[obj.id];
            }
            return obj;
          });

          servicesCache = data;
          servicesCacheTime = Date.now();
          resolve(data);
        } catch (error) {
          console.error(`** Unable to parse the XMLHttpRequest response (${url})`, error);
          resolve(undefined);
        } finally {
          servicesRequest = null;
        }
      };
      xhr.onerror = function () {
        servicesRequest = null;
        resolve(undefined);
        console.error(`** An error occurred during the XMLHttpRequest (${url})`);
      };
      xhr.send();
    });

    return servicesRequest;
  },

  clearServicesCache() {
    servicesCache = null;
    servicesCacheTime = 0;
  },

  /**
   * Retrieve tour availability for each tour on the given tour_date and save in self.tours
   * @param {string} tourDate - Tour date selected by user
   */
  async getTours(tourDate) {
    if (!tourDate) return [];
    let tours = [];
    let services = await this.listServices();

    // build a tours array to work from, removing disabled and sub services
    tours = services
      .filter((obj) => obj.status == 1 && obj.is_subservice == 0) // Ignore addons and ensure the service is enabled
      .map((obj) => ({
        id: obj.id,
        name: obj.name,
        description: obj.description,
        duration: obj.duration,
        ord: obj.ord,
        active: this.isActiveTour(tourDate, obj),
        url: obj.return_url,
        instructions: obj.client_instructions,
        widget_message: obj.widget_message,
      }));

    // Build setster API url
    var baseurl = apiURL + "availability";
    baseurl += "?company_id=" + companyId;
    baseurl += "&start_date=" + tourDate;
    baseurl += "&t=daily&return=times";

    // Get tour times for all tours asynchronously.  Drastically speeds up loading.
    // Create a promise for each tour, 
    let promises = [];
    tours.forEach(function (tour, idx) {
      let url = baseurl + "&service_id=" + tour.id;

      if (tour.active == false) return;

      promises[idx] = new Promise((resolve) => {
        let xhr = new XMLHttpRequest();

        xhr.open("GET", url, true);
        xhr.onload = function () {
          let data = JSON.parse(xhr.response).data;

          tours[idx].times = data.times[tourDate];
          resolve(true);
        };
        xhr.onerror = function () {
          resolve(undefined);
          console.error(`** An error occurred during the XMLHttpRequest (${url})`);
        };
        xhr.send();
      });
    });

    // Wait till all tours are retrieved (async)
    await Promise.all(promises);

    // Do not show tours with no times allocated.
    tours = tours.filter((val) => {
      // Check for times field and if it is not undefined
      if (Object.prototype.hasOwnProperty.call(val, 'times') && val.times != undefined) {
        return val.times.length > 0;
      }
      return false;
    });

    // Sort the tours by their order number
    tours.sort((a, b) => a.ord - b.ord); 

    // Return tours array, populated with available time slots
    return tours;
  },

  /**
   * Calls setster's API to book each tour slot
   * @param {object} selectedTours - List of tours currently selected by user
   * @param {string} selectedDate - Date of tours selected
   * @param {object} form - Form values with user's information
   */
  async bookTours(selectedTours, selectedDate, form) {
    let url = apiURL + "appointment";
    
    let uuid = uuidv4();
    
    // let form = visitorForm;
    
    let promises = [];
    selectedTours.forEach(function (tour, idx) {
      let start_date = `${selectedDate} ${tour.time}`;

      let json_data = {
        data: {
          company_id: companyId,
          client_email: form.email,
          client_name: form.firstname + " " + form.lastname,
          client_phone: form.phone || null,
          service_id: tour.id,
          start_date: start_date,
          note: form.note,
          custom_data: {
            group_size: form.group_size || 1,
            booking_uid: uuid,
            booking_count: selectedTours.length,
            booking_index: idx + 1,
            opt_in: form.optin,
            alumni: form.alumni,
            international: form.international,
            faculty: form.faculty,
            accessibility: form.accessibility,
            entry_year: form.entry_year,
          },
          timezone_id: 548,
        },
      };

      // Call API and create appointments
      promises[idx] = new Promise((resolve, reject) => {
        let xhr = new XMLHttpRequest();

        xhr.open("POST", url, true);

        xhr.setRequestHeader("Content-Type", "application/json");

        xhr.onload = function () {
          let res = JSON.parse(xhr.response);
          let data = {'response': xhr.response, 'tour': tour };
          if (res.statusCode == 0) {
            resolve(data);
          } else {
            reject(data);
          }
        };

        xhr.onerror = function () {
          console.error("** An error occurred during the XMLHttpRequest");
          resolve(undefined);
        };

        xhr.send(JSON.stringify(json_data));
      });
    });

    // Wait till all tours are checked (in parallel)
    return await Promise.allSettled(promises);
    
  },

  /**
   * List tours eligible for cancellation
   * @param {string} guid 
   * @returns array
   */
  getCancelTours(guid) {

    if(!validateGUID(guid)) {
      console.error("Invalid GUID provided");
      return [];
    }

    return new Promise((resolve) => {
      let xhr = new XMLHttpRequest();

      // Build setster api url
      let url = murHost;
      url += "cancel/" + companyId;
      url += "?guid=" + guid;

      // Call api to retrieve available services
      xhr.open("POST", url, false);
      xhr.onload = function () {
        let data = JSON.parse(xhr.responseText);
        resolve(data);
      };
      xhr.onerror = function () {
        resolve(undefined);
        console.error(`** An error occurred during the XMLHttpRequest (${url})`);
      };
      xhr.send();
    });
  },


  /**
   * Cancels a tour for the given id and hash using setster's API
   * @param {int} id 
   * @param {string} hash 
   */
  cancelTour(id, hash) {
    // Get tour times for all tours asynchronously.  Drastically speeds up loading.
    // Create a promise for each tour, 
    let promise = new Promise((resolve) => {
      let xhr = new XMLHttpRequest();

      // Build setster api url
      let url = cancelUrl;
      url += "?id=" + id ;
      url += "&h=" + hash;

      // prepare form data
      let data = new FormData();
      data.append('dodecline', '1');
      data.append('declineReason', 'API: Cancelled via cancellation page.');

      xhr.open("POST", url, false);

      // set headers
      // xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');      

      // Call api to retrieve available services
      
      xhr.onload = function () {
        let data = xhr.responseText;
        resolve(data);
      };
      xhr.onerror = function () {
        resolve(undefined);
        console.error(`** An error occurred during the XMLHttpRequest (${url})`);
      };
      xhr.send(data);
    });

    // Wait till all tours are retrieved (async)
    return promise;
  },

  /**
   * Cancels all tours in a given visit
   * @param {array} tours - Array of tour objects
   */
  cancelAllTours(tours) {
    let promises = [];

    tours.forEach((item, idx) => {
      promises[idx] = this.cancelTour(item.id, item.hash);
    });

    return Promise.all(promises);
  },

  /**
   * Checks if the tour object is active for the day specified
   * @param {*} tourDate - Tour date selectedd in date picker
   * @param {*} obj - Single tour object from iteration
   */
  isActiveTour(tourDate, obj) {
    if (obj.active_rules == 1) {
      return moment(tourDate).isBetween(obj.active_from, obj.active_until, 'day', []);
    }
    return true;
  },
};
